import { useRef, useState } from 'react';
import { useApi } from '../lib/api';
import { qs } from '../lib/util';
import type { CuratedEntity, ImportSummary } from '../lib/types';
import { ErrorBox, Field, Toolbar } from '../ui';

const ENTITIES: { value: CuratedEntity; label: string; base: string; headers: string }[] = [
  { value: 'anticross', label: 'Anticross OE → part', base: '/v1/admin/anticross', headers: 'oe, brand, article, confidence, evidence_count, reason, status' },
  { value: 'anticross-part-part', label: 'Anticross part ↔ part', base: '/v1/admin/anticross/part-part', headers: 'a_brand, a_article, b_brand, b_article, status, confidence, reason' },
  { value: 'xref-oe-part', label: 'Cross OE → part', base: '/v1/admin/xref/oe-part', headers: 'oe, brand, article, name, confidence, reason' },
  { value: 'xref-part-part', label: 'Cross part ↔ part', base: '/v1/admin/xref/part-part', headers: 'a_brand, a_article, b_brand, b_article, relation, confidence, reason' },
];

function Summary({ s }: { s: ImportSummary }) {
  return (
    <div className="mt-3 rounded-md border border-line p-3 text-[12px]">
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        <span className="text-muted">{s.dryRun ? 'Dry-run' : 'Applied'}</span>
        <span>
          <b>{s.total}</b> rows
        </span>
        <span className="text-ok">{s.ok} ok</span>
        {s.failed > 0 && <span className="text-bad">{s.failed} failed</span>}
        {s.refs != null && <span className="text-muted">{s.refs} refs</span>}
      </div>
      {s.errors.length > 0 && (
        <div className="mono mt-2 max-h-40 overflow-auto text-[11.5px] text-bad">
          {s.errors.map((e, i) => (
            <div key={i}>
              {e.row != null ? `row ${e.row}: ` : ''}
              {e.message ?? JSON.stringify(e)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ImportExport() {
  const { request, download } = useApi();
  const [entity, setEntity] = useState<CuratedEntity>('anticross');
  const [json, setJson] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const cfg = ENTITIES.find((e) => e.value === entity)!;
  const bulk = `${cfg.base}/bulk`;

  async function run(dry: boolean) {
    setErr('');
    setSummary(null);
    const file = fileRef.current?.files?.[0];
    let body: FormData | unknown[];
    if (file) {
      const fd = new FormData();
      fd.append('file', file);
      body = fd;
    } else if (json.trim()) {
      try {
        const parsed = JSON.parse(json);
        if (!Array.isArray(parsed)) throw new Error('must be a JSON array of rows');
        body = parsed;
      } catch (e) {
        setErr(`Invalid JSON: ${(e as Error).message}`);
        return;
      }
    } else {
      setErr('Choose a CSV/XLSX file or paste a JSON array first.');
      return;
    }
    setBusy(true);
    const res = await request<ImportSummary>('POST', `${bulk}${qs({ dryRun: dry ? 'true' : undefined })}`, body);
    setBusy(false);
    if (res.data && typeof res.data === 'object' && 'total' in res.data) setSummary(res.data);
    else setErr(`Import failed (HTTP ${res.status}).`);
  }

  return (
    <div className="grid gap-3.5" style={{ gridTemplateColumns: '1fr 1fr' }}>
      <div className="blk !mb-0">
        <div className="blk-head">
          <h3 className="text-[13px] font-semibold">Import</h3>
          <span className="text-[11.5px] text-faint">CSV, XLSX or JSON</span>
        </div>
        <div className="blk-body">
          <Toolbar>
            <select className="input" style={{ width: 'auto' }} value={entity} onChange={(e) => setEntity(e.target.value as CuratedEntity)}>
              {ENTITIES.map((e) => (
                <option key={e.value} value={e.value}>
                  {e.label}
                </option>
              ))}
            </select>
          </Toolbar>
          <Field label={`File — headers: ${cfg.headers}`}>
            <input ref={fileRef} className="input" type="file" accept=".csv,.xlsx,.xls" onChange={() => setJson('')} />
          </Field>
          <div className="my-2 text-center text-[11px] text-faint">— or paste a JSON array —</div>
          <textarea
            className="input mono"
            rows={5}
            placeholder='[{"oe":"…","brand":"…","article":"…"}]'
            value={json}
            onChange={(e) => setJson(e.target.value)}
          />
          <Toolbar className="!mb-0 mt-3">
            <button className="btn btn-primary" onClick={() => run(true)} disabled={busy}>
              Validate (dry-run)
            </button>
            <button className="btn" onClick={() => run(false)} disabled={busy}>
              Apply
            </button>
          </Toolbar>
          {err && <div className="mt-3"><ErrorBox>{err}</ErrorBox></div>}
          {summary && <Summary s={summary} />}
        </div>
      </div>

      <div className="blk !mb-0">
        <div className="blk-head">
          <h3 className="text-[13px] font-semibold">Export</h3>
          <span className="text-[11.5px] text-faint">current curated datasets</span>
        </div>
        <div className="blk-body space-y-2.5">
          {ENTITIES.map((e) => (
            <div key={e.value} className="flex items-center gap-2">
              <span className="flex-1">{e.label}</span>
              <button className="btn btn-sm" onClick={() => download(`${e.base}/export${qs({ format: 'csv' })}`, `${e.value}.csv`)}>
                CSV
              </button>
              <button className="btn btn-sm" onClick={() => download(`${e.base}/export${qs({ format: 'xlsx' })}`, `${e.value}.xlsx`)}>
                XLSX
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
